import React, { CSSProperties, HTMLAttributes, ReactElement, useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import styled from 'styled-components';

export interface FontEditorProps {
  /**
   * Initial content styles
   */
  defaultValue?: Pick<CSSProperties, 'fontFamily' | 'fontSize' | 'color' | 'textDecoration' | 'fontWeight' | 'fontStyle'>;
  /**
   * Event emit
   */
  fireEvent: 'click' | 'hover' | 'none';
  /**
   * Element content
   */
  label: ReactElement | string | number;
  /**
   * Font size list
   */
  sizes?: {
    [Symbol in string]: number;
  };
  /**
   * Font family list check in `https://fonts.googleapis.com` for more info
   */
  fonts?: {
    [Symbol in string]: string;
  };
  /**
   * On style change event handler
   */
  onStyleChanged?: (value: CSSProperties) => void;
  /**
   * Enable size option
   */
  withSize?: boolean;
  /**
   * Enable font color option
   */
  withColor?: boolean;
  /**
   * Enable text decoration option
   */
  withDecoration?: boolean;
}

export const useTooltipController = (args?: Omit<FontEditorProps, 'label'>) => {
  const [toggle, setToggle] = useState(false);
  const [fontFamily, setFontFamily] = useState<string>(args?.defaultValue?.fontFamily || '');
  const [fontSize, setFontSize] = useState<number | string>(args?.defaultValue?.fontSize || 16);
  const [color, setColor] = useState(args?.defaultValue?.color || '#000');
  const [fontWeight, setFontWeight] = useState<'normal' | 'bold'>((args?.defaultValue?.fontWeight as 'normal' | 'bold') || 'normal');
  const [fontStyle, setFontStyle] = useState<'normal' | 'italic'>((args?.defaultValue?.fontStyle as 'normal' | 'italic') || 'normal');
  const [textDecoration, setTextDecoration] = useState<'none' | 'underline'>(
    (args?.defaultValue?.textDecoration as 'none' | 'underline') || 'none',
  );

  const [fonts, setFonts] = useState<{
    [Symbol in string]: string;
  }>({
    Roboto: 'Roboto+sans-serif',
    'Open Sans': 'Open Sans+sans-serif',
    Lato: 'Lato, sans-serif',
    Montserrat: 'Montserrat, sans-serif',
    Poppins: 'Poppins, sans-serif',
    'Source Sans Pro': 'Source Sans Pro, sans-serif',
    Ubuntu: 'Ubuntu, sans-serif',
    Oswald: 'Oswald, sans-serif',
    'Roboto Condensed': 'Roboto Condensed, sans-serif',
    'Playfair Display': 'Playfair Display, serif',
    Merriweather: 'Merriweather, serif',
    Quicksand: 'Quicksand, sans-serif',
    'Josefin Sans': 'Josefin Sans, sans-serif',
    'Montserrat Alternates': 'Montserrat Alternates, sans-serif',
    Oxygen: 'Oxygen, sans-serif',
    Raleway: 'Raleway, sans-serif',
    'Titillium Web': 'Titillium Web, sans-serif',
    'Varela Round': 'Varela Round, sans-serif',
    Archivo: 'Archivo, sans-serif',
    Cabin: 'Cabin, sans-serif',
    'Advent Pro': 'Advent Pro, cursive',
    'Black Ops One': 'Black Ops One, cursive',
    Cookie: 'Cookie, cursive',
    Dosis: 'Dosis, cursive',
    'Great Vibes': 'Great Vibes, cursive',
    'Indie Flower': 'Indie Flower, cursive',
    'Kaushan Script': 'Kaushan Script, cursive',
    Lobster: 'Lobster, cursive',
    Pacifico: 'Pacifico, cursive',
    Sacramento: 'Sacramento, cursive',
  });
  const [sizes, setSizes] = useState<{
    [Symbol in string]: number;
  }>({
    8: 8,
    9: 9,
    10: 10,
    11: 11,
    12: 12,
    14: 14,
    16: 16,
    18: 18,
    20: 20,
    22: 22,
    24: 24,
    26: 26,
    28: 28,
    36: 36,
    48: 48,
    72: 72,
  });

  useEffect(() => {
    args?.sizes && setSizes(args.sizes);
    args?.fonts && setFonts(args.fonts);
  }, [args]);

  const link = useMemo(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css?family=${Object.keys(fonts).join('|')}`;

    return link;
  }, [fonts]);

  useEffect(() => {
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, [link]);

  const style: CSSProperties = useMemo(() => {
    return {
      fontFamily,
      fontSize,
      fontStyle,
      textDecoration,
      fontWeight,
      color,
    };
  }, [fontFamily, fontSize, fontStyle, textDecoration, color, fontWeight]);

  const toggleHandler = (state?: boolean) => setToggle((prev) => (state !== undefined ? state : !prev));

  const containerProps: Pick<HTMLAttributes<HTMLDivElement>, 'onClick' | 'onMouseOver' | 'onMouseLeave' | 'onDoubleClick' | 'className'> = {
    className: 'tooltip-container',
    onClick: args?.fireEvent === 'click' ? () => toggleHandler(!toggle) : undefined,
    onMouseOver: args?.fireEvent === 'hover' ? () => toggleHandler(true) : undefined,
    onDoubleClick: () => toggleHandler(false),
  };

  const colorPreviewStyles: CSSProperties = {
    backgroundColor: color,
  };

  const tooltipHoverHandler = () => toggleHandler(true);

  const fontFamilyHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFontFamily(e.target.value);
    toggleHandler(false);
  };

  const fontSizeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFontSize(parseFloat(e.target.value));
    toggleHandler(false);
  };

  const colorHandler = (e: React.ChangeEvent<HTMLInputElement>) => setColor(e.target.value);

  const fontWeightHandler = () => setFontWeight((prev) => (prev === 'bold' ? 'normal' : 'bold'));

  const textDecorationHandler = () => setTextDecoration((prev) => (prev === 'underline' ? 'none' : 'underline'));

  const fontStyleHandler = () => setFontStyle((prev) => (prev === 'italic' ? 'normal' : 'italic'));

  useEffect(() => {
    args?.onStyleChanged?.(style);
  }, [args, style]);

  return {
    fonts,
    sizes,
    style,
    toggle,
    toggleHandler,
    containerProps,
    tooltipHoverHandler,
    fontFamilyHandler,
    fontSizeHandler,
    colorPreviewStyles,
    colorHandler,
    fontWeightHandler,
    textDecorationHandler,
    fontStyleHandler,
  };
};

/**
 * Primary UI component for user interaction
 */
export default function FontEditor({
  label,
  withColor = true,
  withDecoration = true,
  withSize = true,
  fireEvent = 'click',
  ...args
}: FontEditorProps) {
  const {
    fonts,
    sizes,
    toggle,
    containerProps,
    tooltipHoverHandler,
    fontFamilyHandler,
    fontSizeHandler,
    colorHandler,
    colorPreviewStyles,
    fontWeightHandler,
    textDecorationHandler,
    fontStyleHandler,
    toggleHandler,
    style,
  } = useTooltipController({
    ...args,
    withColor,
    withDecoration,
    withSize,
    fireEvent,
  });

  const modal = (
    <>
      {toggle && (
        <div onMouseOver={tooltipHoverHandler} className="tooltip-content">
          <div onClick={(e) => e.stopPropagation()} className="tooltip-content-container">
            <div>
              <select
                style={{
                  fontFamily: 'sans-serif',
                  textDecoration: 'none',
                  fontWeight: 'normal',
                  fontStyle: 'normal',
                }}
                value={style.fontFamily}
                onChange={fontFamilyHandler}
                className="tooltip-custom-select"
              >
                {Object.keys(fonts).map((item, key) => (
                  <option
                    style={{
                      fontFamily: fonts[item],
                      textDecoration: 'none',
                      fontWeight: 'normal',
                      fontStyle: 'normal',
                    }}
                    key={key}
                    label={item.toString()}
                    value={fonts[item]}
                  />
                ))}
              </select>
            </div>
            {withSize && (
              <div>
                <select
                  style={{
                    fontFamily: 'sans-serif',
                    textDecoration: 'none',
                    fontWeight: 'normal',
                    fontStyle: 'normal',
                  }}
                  value={style.fontSize}
                  onChange={fontSizeHandler}
                  className="tooltip-custom-select"
                >
                  {Object.keys(sizes).map((item, key) => (
                    <option
                      style={{
                        fontFamily: 'sans-serif',
                        textDecoration: 'none',
                        fontWeight: 'normal',
                        fontStyle: 'normal',
                      }}
                      key={key}
                      label={item.toString()}
                      value={sizes[item]}
                    />
                  ))}
                </select>
              </div>
            )}
            {withDecoration && (
              <div className="tooltip-label-style">
                <label onClick={fontWeightHandler} className="tooltip-label tooltip-label-bold">
                  B
                </label>
                <label onClick={fontStyleHandler} className="tooltip-label tooltip-label-italic">
                  i
                </label>
                <label onClick={textDecorationHandler} className="tooltip-label tooltip-label-underline">
                  S
                </label>
              </div>
            )}
            {withColor && (
              <div className="tooltip-color-picker">
                <input onChange={colorHandler} type="color" className="tooltip-custom-color-input" />
                <div
                  style={{
                    ...colorPreviewStyles,
                  }}
                  className="tooltip-color-preview"
                ></div>
              </div>
            )}
            <span onClick={() => toggleHandler(false)} style={{ marginRight: 10 }}>
              <FaTimes color="red" size={20} />
            </span>
          </div>
        </div>
      )}
    </>
  );

  const element =
    typeof label !== 'string' && typeof label !== 'number' ? (
      React.cloneElement(
        label,
        {
          className: 'tooltip-label',
          style,
          ...containerProps,
        },
        <>
          {label.props.children}
          {modal}
        </>,
      )
    ) : (
      <div className="tooltip-label" style={{ ...style }} {...containerProps}>
        {label}
        {modal}
      </div>
    );

  return <Container>{element}</Container>;
}

const Container = styled.span`
  max-width: auto !important;

  .tooltip-container {
    display: flex;
    position: relative;
  }

  .tooltip-label {
    cursor: pointer;
    z-index: 1;
    font-family: sans-serif;
    font-size: 16px;
  }

  .tooltip-content {
    position: absolute;
    margin-top: -70px;
    background-color: transparent;
    left: 50%;
    transform: translate(-50%, 0);
    min-height: 100px;
  }

  .tooltip-content-container {
    display: flex;
    flex-direction: flex;
    gap: 0;
    align-items: center;
    background-color: white;
    padding: 0.2rem 0;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    min-height: 50px;
  }

  .tooltip-content-container > * {
    display: flex;
    user-select: none;
    text-wrap: nowrap;
    padding: 0.2rem 0.5rem;
    max-height: 48px;
  }

  .tooltip-content-container > *:last-child {
    border-right: none;
  }

  .tooltip-custom-select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    padding: 8px 12px;
    font-size: 16px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    outline: none;
    background: #fff
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='rgba(0, 0, 0, 0.2)'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3Cpath fill='none' d='M0 0h24v24H0z'/%3E%3C/svg%3E")
      no-repeat right 12px center;
    background-size: 16px;
    background-color: #fff;
    min-width: 80px;
    height: 38px;
  }

  .tooltip-custom-select:hover {
    border: 1px solid #3498db;
    background: #fff
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%233498db'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3Cpath fill='none' d='M0 0h24v24H0z'/%3E%3C/svg%3E")
      no-repeat right 12px center;
    background-size: 16px;
    background-color: #fff;
  }

  .tooltip-label-style {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
    height: 38px;
  }

  .tooltip-label-bold {
    color: #000 !important;
    font-family: sans-serif !important;
    font-weight: bold !important;
    font-style: normal !important;
  }

  .tooltip-label-italic {
    font-style: italic !important;
    font-family: sans-serif !important;
    font-weight: normal !important;
    color: #000 !important;
  }

  .tooltip-label-underline {
    position: relative;
    text-decoration: underline !important;
    font-family: sans-serif !important;
    font-weight: normal !important;
    color: #000 !important;
    font-style: normal !important;
  }

  .tooltip-color-picker {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tooltip-custom-color-input {
    position: absolute;
    padding: 0;
    border: none;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    opacity: 0;
    cursor: pointer;
  }

  .tooltip-color-preview {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #ccc;
    cursor: pointer;
  }

  @media only screen and (max-width: 767px) {
    .tooltip-content {
      position: fixed;
      bottom: 0 !important;
      background-color: rgba(0, 0, 0, 0.1);
      width: 100vw;
      height: 100vh;
      z-index: 99999 !important;
    }

    .tooltip-content-container {
      flex-direction: column;
      padding: 0.5rem 1rem;
    }

    .tooltip-content-container > * {
      border: none;
    }

    .tooltip-content-container {
      display: grid;
      grid-template-columns: 1fr auto;
      grid-template-rows: auto;
      position: absolute;
      bottom: 0;
      width: 100%;
      border-radius: 0;
    }

    .tooltip-custom-select {
      min-width: 100%;
    }
  }
`;
